import { useState } from "react";
import Decimal from "decimal.js";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { BirthdayFundRequest, useExpireRequest } from "@/lib/api/fund";

interface ExpireRequestConfirmDialogProps {
  request: BirthdayFundRequest | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExpired?: () => void;
}

export function ExpireRequestConfirmDialog({
  request,
  open,
  onOpenChange,
  onExpired,
}: ExpireRequestConfirmDialogProps) {
  const { toast } = useToast();
  const expireRequest = useExpireRequest();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [backendError, setBackendError] = useState<string | null>(null);

  if (!request) return null;

  // Normalize breakdowns defensively
  const breakdowns = (() => {
    if (!request.breakdowns) return [];
    if (Array.isArray(request.breakdowns)) return request.breakdowns;
    if ((request.breakdowns as any).id) return [request.breakdowns];
    return [];
  })();

  // Compute granted breakdown sum using Decimal
  let grantedBreakdownSum = new Decimal(0);
  for (const breakdown of breakdowns) {
    if (breakdown.status === "granted") {
      const amount = breakdown.amount ? new Decimal(breakdown.amount) : new Decimal(0);
      grantedBreakdownSum = grantedBreakdownSum.plus(amount);
    }
  }

  // Determine warning level
  let warningLevel: "none" | "orange" | "red" = "none";
  if (request.has_been_granted) {
    if (grantedBreakdownSum.isZero()) {
      warningLevel = "orange";
    } else if (grantedBreakdownSum.greaterThan(0)) {
      warningLevel = "red";
    }
  }

  const getTitle = () => {
    if (warningLevel === "orange") {
      return "Expire Granted Request";
    }
    if (warningLevel === "red") {
      return "Expire Granted Request with Breakdown Amounts";
    }
    return `Mark Birthday Fund Request #${request.birthday_fund_number} as Expired?`;
  };

  const getDescription = () => {
    if (warningLevel === "orange") {
      return "This request is marked granted but has no granted breakdown amount. Are you sure you want to expire it?";
    }
    if (warningLevel === "red") {
      return "This request has granted breakdown amounts. Expiring it will remove it from active stats and reports. Are you sure?";
    }
    return `This will mark birthday fund request #${request.birthday_fund_number} as expired.`;
  };

  const handleConfirm = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setBackendError(null);

    try {
      const result = await expireRequest({
        id: request.id,
        confirmation_acknowledged: request.has_been_granted ? true : undefined,
      });

      if (result.success) {
        toast({ title: "Request expired" });
        onOpenChange(false);
        onExpired?.();
      } else {
        // Backend returned an error (e.g., missing confirmation acknowledgment)
        const message = result.message || "Failed to expire request";
        setBackendError(message);
      }
    } catch (error: any) {
      const message = error?.message || "Failed to expire request";
      setBackendError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-2xl lg:max-w-3xl xl:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
          <DialogDescription>
            {getDescription()}
          </DialogDescription>
        </DialogHeader>

        {/* Warning level indicator */}
        {warningLevel === "orange" && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-orange-800 text-sm">
            This request is marked as granted but has no granted breakdown amounts.
          </div>
        )}
        {warningLevel === "red" && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">
            This request has granted breakdown amounts totaling {grantedBreakdownSum.toFixed(2)}. Expiring will remove it from active stats.
          </div>
        )}

        {/* Backend error */}
        {backendError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">
            {backendError}
          </div>
        )}

        {/* Request info */}
        <div className="rounded-lg border bg-muted/50 p-4">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div>
              <span className="text-muted-foreground">Birthday Fund ID:</span>
              <span className="ml-2 font-semibold">#{request.birthday_fund_number}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Nominator:</span>
              <span className="ml-2">{request.survey.data.nominator_first_name} {request.survey.data.nominator_last_name}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Organization:</span>
              <span className="ml-2">{request.nominated_organization}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Status:</span>
              <span className="ml-2">{request.has_been_granted ? "Granted" : "Not Granted"}</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant={warningLevel === "red" ? "destructive" : "default"}
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Expiring..." : "Confirm Expire"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
